/**
 * Web Push registration (docs/references/web-push-pwa.md). The VAPID public key is public; the
 * private half never leaves the api. The first `subscribe()` must run inside a real user gesture,
 * which is why `enablePushNotifications` is only ever called from a click handler.
 */
import { api } from './api'
import { VAPID_PUBLIC_KEY } from './env'

export type PushCapability = 'unsupported' | 'ios-tab' | 'available'

export type ServiceWorkerMessage = {
  type: 'notificationclick' | 'notificationclose' | 'pushsubscriptionchange'
  url?: string
  data?: Record<string, unknown>
  oldEndpoint?: string | null
  subscription?: { endpoint: string; keys?: { p256dh: string; auth: string } } | null
}

function isIos(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

export function isInstalledPwa(): boolean {
  const standalone = (navigator as Navigator & { standalone?: boolean }).standalone === true
  return (
    standalone ||
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches
  )
}

export function pushCapability(): PushCapability {
  if (!('serviceWorker' in navigator)) return 'unsupported'
  if (isIos() && !isInstalledPwa()) return 'ios-tab'
  if (typeof Notification === 'undefined' || typeof PushManager === 'undefined') return 'unsupported'
  return 'available'
}

/** iOS shows no `beforeinstallprompt`; installing is a manual Share-sheet action. */
export function needsIosInstallHint(): boolean {
  return isIos() && !isInstalledPwa()
}

function urlBase64ToUint8Array(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (base64url.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = new Uint8Array(new ArrayBuffer(binary.length))
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  return bytes
}

/** Waits for the worker registered by `useRegisterSW`; resolves null when none ever activates. */
async function activeRegistration(timeoutMs = 5000): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
  ])
}

export async function enablePushNotifications(): Promise<'granted' | 'denied' | 'unsupported' | 'ios-tab'> {
  const capability = pushCapability()
  if (capability !== 'available') return capability
  if (Notification.permission !== 'granted') {
    // First await must be the permission prompt: iOS only prompts while the gesture is alive.
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return 'denied'
  }

  const registration = await activeRegistration()
  if (!registration) return 'unsupported'

  const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
  // Browsers require the 65-byte uncompressed P-256 point (web-push-pwa.md §Key material).
  if (applicationServerKey.length !== 65) return 'unsupported'
  let subscription = await registration.pushManager.getSubscription()
  if (subscription?.options.applicationServerKey) {
    const current = new Uint8Array(subscription.options.applicationServerKey)
    const sameKey =
      current.length === applicationServerKey.length &&
      current.every((byte, index) => byte === applicationServerKey[index])
    if (!sameKey) {
      // The VAPID key rotated: the old subscription can never be delivered to.
      await subscription.unsubscribe()
      subscription = null
    }
  }
  subscription ??= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey })

  const json = subscription.toJSON()
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return 'unsupported'
  await api.subscribePush({
    kind: 'webpush',
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    ua: navigator.userAgent,
  })
  return 'granted'
}

/**
 * Re-registers the existing subscription on app open. Endpoints rotate and the api prunes dead
 * ones, so this is what keeps ringing alive for an installed PWA.
 */
export async function syncPushSubscription(): Promise<void> {
  if (pushCapability() !== 'available' || Notification.permission !== 'granted') return
  const registration = await activeRegistration()
  const subscription = await registration?.pushManager.getSubscription()
  const json = subscription?.toJSON()
  if (!json?.endpoint || !json.keys?.p256dh || !json.keys.auth) return
  await api
    .subscribePush({
      kind: 'webpush',
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      ua: navigator.userAgent,
    })
    .catch(() => undefined)
}

/** Routes taps on notifications into the SPA (the SW also sets `navigate` for iOS). */
export function onServiceWorkerMessage(handler: (message: ServiceWorkerMessage) => void): () => void {
  if (!('serviceWorker' in navigator)) return () => undefined
  const listener = (event: MessageEvent) => {
    const data: unknown = event.data
    if (typeof data !== 'object' || data === null || !('type' in data)) return
    handler(data as ServiceWorkerMessage)
  }
  navigator.serviceWorker.addEventListener('message', listener)
  return () => navigator.serviceWorker.removeEventListener('message', listener)
}
